/**
 * Copyright(c) 2023 Magrabi.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @author Indrajith C
 */

import { SetStateAction } from "react";
import {
  BrandEdges,
  BrandNode,
  CheckoutLines,
  CityType,
  FacetItem,
  FacetItemWithImage,
  FacetMappingType,
  FacetStyle,
  GAUserObject,
  GeoLocation,
  GroupedBrands,
  GroupedCategories,
  LinkContent,
  PopularBrandItem,
  Recommendation,
  RecommendationType,
  StoreAddress,
  StoreLocation,
  VariantItem,
  TypeSessionUser,
  FacetColorMappingType,
  GAInitialObject,
  WorkCalendarType,
  EyeTestTimeSlot,
  CalendarEventDetails,
  VariantItemPdp,
  BreadcrumbItem,
  CategoryDetails,
  ShippingMethod,
  GaPageInfo,
  Checkout,
  Order,
  CheckoutResponse,
  PersonalDetailsInputType,
} from "../common/lib/types";
import {
  deviceTypes,
  plpLayoutConfig,
  recommendationType,
  paymentConstants,
  dateFormats,
  vtoConstants,
  addressCountryMappings,
  ourBrandsFields,
  sessionFlowConstants,
  analyticEventTypes,
  sessionQueryParams,
  allowedPrescriptionFileTypes,
  ProductType,
  eyeTestServiceType,
  bookingStatus,
  bookingStatusArabic,
  SaleorAttributes,
  siteIds,
  localizedIndexes,
  monthMap,
  blogsInternalUrlQueryParams,
  heifFileTypes,
  analyticEventSources,
  sessionStorageKeys,
  shippingConstants,
  facetTypeConstant,
  defaultTranslations,
  BrandMainCategories,
  arAlphabets,
  enAlphabets,
  promotionType,
  giftTypes,
} from "../apiUtils/constants";
import { RecommendProductItem, SortItem } from "../apiUtils/algoliaUtils/types";
import { RecommendModels } from "@algolia/recommend";
import { i18n } from "../apiUtils/i18n/config";
import { analyticEvents, handlePostAnalytics } from "./analyticsUtils";
import { ReadonlyURLSearchParams } from "next/navigation";
import * as crypto from "crypto";
import { getAttributeValue } from "./productUtils";
import { filterIconUrl } from "../utils/filterIcon";
import {
  defaultLocale,
  hasFallBackTranslation,
  locales,
  revalidateTime,
} from "../runtimeConfig/public";
import { algoliaGuestToken, productListingPageKey } from "./algoliaUtils/constants";
import { getFromCookieMetadataSA, setInCookieMetadataSA } from "./cookieDataUtilsServerActions";
import parsePhoneNumberFromString, { isValidPhoneNumber } from "libphonenumber-js/max";
import { currencyMappings } from "@/runtimeConfig/server";
/**
 * This function is used to parse locale for db format
 * @param {String} locale
 * @returns
 */
export const localeParserForApi = (locale: string) => {
  let dbLocale = null;
  try {
    const [language, country] = locale.split("-");
    if (language && country) {
      dbLocale = `${language}_${`${country}`.toUpperCase()}`;
    }
  } catch (error) {
    console.error(error);
  }
  return dbLocale;
};

/**
 * This function is used to parse locale for ui format
 * @param {String} locale
 * @returns
 */
export const localeParserForUi = (locale: string) => {
  let uiLocale = null;
  try {
    uiLocale = locale.toLowerCase().replace("_", "-");
  } catch (error) {
    console.error(error);
  }
  return uiLocale;
};

/**
 * This function is used to get lang from a locale
 *
 * @param {string} locale
 * @return {*}  {string}
 */
export const getLangFromLocale = (locale: string): string => {
  return locale ? locale.split("-")[1] : "";
};

/**
 * This function is used to get country code from a locale
 *
 * @param {string} locale
 * @return {*}  {string}
 */
export const getCountryCodeFromLocale = (locale: string): string => {
  return locale ? locale.split("-")[0] : "";
};

/**
 * This function is used to get siteId from a locale
 *
 * @param {string} locale
 * @return {*}  {string}
 */
export const getSiteIdFromLocale = (locale: string): string => {
  return locale ? siteIds?.[locale] : "";
};

/**
 * Sanitizes a mobile number for POS/API: strips non-digits and removes the country dial code prefix if present.
 * Supports multiple countries via addressCountryMappings.
 */
export const sanitizeMobileForCountry = (
  mobile: string | undefined,
  countryCode: string,
): string | undefined => {
  if (!mobile) return undefined;
  let sanitized = mobile.replaceAll(/\D/g, "");
  const country = countryCode ? addressCountryMappings[countryCode.toLowerCase()] : undefined;
  const dialCodeDigits = country?.dialCode?.replaceAll(/\D/g, "");
  if (dialCodeDigits && sanitized.startsWith(dialCodeDigits)) {
    sanitized = sanitized.slice(dialCodeDigits.length);
  }
  return sanitized || undefined;
};

/**
 * This function checks if a list or array is  not null or not empty
 * @param {Array} list
 */
export const hasArrayElements = (list?: any[] | null): list is any[] =>
  Array.isArray(list) && list.length > 0;

/**
 * @description This function is used chunk array by size
 *
 * @template T
 * @param {Array<T>} inputArray
 * @param {number} chunkSize
 * @return {*}  {Array<Array<T>>}
 */
export const chunkArrayBySize = <T>(inputArray: Array<T>, chunkSize: number): Array<Array<T>> => {
  const chunkedArray = [];
  for (let i = 0; i < inputArray.length; i += chunkSize) {
    chunkedArray.push(inputArray.slice(i, i + chunkSize));
  }
  return chunkedArray;
};

/**
 * THis function used to find if the site rendered device is desktop or mobile
 * @param element Current element to find width
 * @returns deviceTypes
 */
export const findDevice = (element: any) =>
  element?.offsetWidth <= plpLayoutConfig.WIDTH_THRESHOLDS.MEDIUM
    ? deviceTypes.MOBILE
    : deviceTypes.DESKTOP;

/**
 * Utility for transforming facets
 * @param items
 * @param initialFacets
 * @param facetOptions
 * @param styles
 * @returns
 */
export const transformFacets = (
  items: FacetItem[],
  initialFacets: FacetItem[],
  facetOptions: FacetMappingType[],
  styles: FacetStyle,
  facetColorMappings?: FacetColorMappingType[],
) => {
  const updatedItems: FacetItemWithImage[] | [] = Array.isArray(initialFacets)
    ? initialFacets
      .map((initialFacet) => {
        const selectedFacet = Array.isArray(facetOptions)
          ? facetOptions.find(
            (attribute) =>
              attribute?.value === initialFacet?.value ||
              attribute?.code === initialFacet?.value?.toLowerCase(),
          )
          : null;
        // items is the current facets containing products
        const updatedFacetData = items?.find((item) => item.value === initialFacet.value);
        const hexCode =
          facetColorMappings &&
          hasArrayElements(facetColorMappings) &&
          facetColorMappings.find((color) => color.name === initialFacet.value);
        const imageUrl =
          filterIconUrl(
            selectedFacet?.attributeCode ?? "",
            selectedFacet?.code ?? "",
            selectedFacet?.mappingRevision,
          ) ??
          `/magrabi/images/${styles?.noImage}`;
        return updatedFacetData
          ? {
            ...initialFacet,
            imageUrl,
            count: updatedFacetData.count,
            isRefined: !!updatedFacetData.isRefined,
            ...hexCode,
          }
          : { ...initialFacet, imageUrl, count: 0, isRefined: false, ...hexCode };
      })
      ?.filter((item) => item.count > 0)
    : [];
  return updatedItems.sort((a, b) => b.count - a.count);
};

/**
 * Utility to transform PopularBrands Facets
 * @param attribute
 * @param items
 * @param popularBrands
 * @returns
 */
export const transformPopularBrands = (
  items: FacetItemWithImage[],
  popularBrands: PopularBrandItem[],
  language?: string,
) => {
  return items
    .map((item) => {
      const matchingBrand = popularBrands?.find((brand) =>
        language === "ar" ? brand.translation?.name === item.value : brand?.name === item?.value,
      );
      if (matchingBrand) {
        return {
          count: item.count,
          isRefined: item.isRefined,
          value: item.value,
          label: item.label,
          imageUrl: matchingBrand.backgroundImage ? matchingBrand.backgroundImage.url : "",
        } as FacetItemWithImage; // Type assertion to enforce type correctness
      }
      return null;
    })
    .filter((item): item is FacetItemWithImage => item !== null); // Filter out null values
};

/**
 * This function used calculate the percentage.
 * @param total total value
 * @param value current value
 * @returns percentage
 */
export const percentageCalculator = (total: number, value: number) =>
  toPrecise((value / total) * 100);

/**
 * This function is used to precise the number
 * @param {Number} number
 * @param {Number} decimalPoint
 */
export const toPrecise = (number: number, decimalPoint: number = 2) =>
  number ? Math.ceil(number * 10 ** Number(decimalPoint)) / 10 ** Number(decimalPoint) : 0;
/**
 * This function used calculate the percentaged value.
 * @param total total value
 * @param value percentage value
 * @returns percentaged value
 */
export const percentagedValueCalculator = (total: number, percentage: number) =>
  (total * percentage) / 100;

/**
 * This function is used to convert mapEdges to Items
 * @param {*} data
 * @returns
 */
export const mapEdgesToItems = (data: any) =>
  (data?.edges || []).map(({ node }: { node: any }) => node);

/**
 * This Method return the current selected sort label
 * @param value
 * @param index
 * @param language
 * @returns
 */
export const constructIndexName = (value: string | null, index: string, language: string) =>
  value && localizedIndexes.includes(value) ? `${index}_${language}` : index;

/**
 * This Method return the current selected sort label
 * @param options
 * @param currentRefinement
 * @returns
 */
export const getCurrentSort = (
  options: SortItem[],
  currentRefinement: string,
  language: string,
) => {
  const foundOption = options?.find(
    (option) => constructIndexName(option.value, option.index, language) === currentRefinement,
  );
  return foundOption ? foundOption.label : "";
};

/**
 * This Method return the cartId based on the location
 * @param locale
 * @returns cartId
 */
export const getCartIdByLocale = (locale: string) => {
  console.log(
    "getCartIdByLocale locale",
    locale,
    `c_${getCountryCodeFromLocale(locale)}`.toLowerCase(),
  );

  return `c_${getCountryCodeFromLocale(locale)}`.toLowerCase();
};

/**
 * This Method return the wishListId based on the location
 * @param locale string
 * @returns wishlistId
 */
export const getWishlistIdByLocale = (locale: string) => {
  return `w_${getCountryCodeFromLocale(locale)}`.toLowerCase();
};

/**
 * @description Performs fuzzy search on an array of items based on the specified value.
 * @param {string} value - The search value to perform fuzzy search.
 * @param {FacetItemWithImage[]} items - The array of items to be searched.
 * @param {Function} callback - The callback function to update the state with the filtered items.
 */
export const fuzzySearch = (
  value: string,
  items: FacetItemWithImage[],
  callback: (value: SetStateAction<FacetItemWithImage[]>) => void,
): void => {
  // If the search value is empty, return all items
  if (!value.trim()) {
    callback(items);
    return;
  }

  // Construct a regular expression for fuzzy matching based on the search value
  const regex = new RegExp("^" + value.toLowerCase().split("").join(".*"));

  // Perform fuzzy search by filtering items that match the regular expression and sort them by alphabetical order
  const result = items
    .filter((item) => regex.test(item.value.toLowerCase()))
    ?.sort((a, b) => a?.value?.localeCompare(b?.value));

  // Filter out remaining items that do not match the fuzzy search
  const remainingItems = items.filter((item) => !result.includes(item));

  // Update the state with the filtered items
  callback([...result, ...remainingItems]);
};

/**
 * This function is used to get cart count
 *
 * @param {CheckoutLines[]} lines
 * @return {number}
 */
export const getCartCount = (lines: CheckoutLines[]): number => {
  return (
    lines?.reduce((total, item) => {
      // Unified Gift Selection: Exclude auto-added gifts from cart count
      // Order-level gifts
      if (item.isGift) {
        return total;
      }

      // Buy X Get Y auto-added gifts
      const promotion = item?.appliedPromotions?.[0];
      if (
        promotion?.promotion?.type === promotionType.BUYXGETY &&
        promotion?.giftType === giftTypes.FREE_GIFT
      ) {
        return total;
      }

      // Regular items and add-ons
      if (!item?.parent) {
        return total + (item?.quantity || 0);
      } else {
        // If the item is a gift, not adding in the total count
        return total;
      }
    }, 0) ?? 0
  );
};

/**
 * Formats the order summary item count using Sanity cart translations (e.g. "3 Items", "1 Item").
 */
export const formatOrderSummaryItemCount = (
  count: number,
  translations?: Record<string, string>,
): string => {
  const label =
    count === 1
      ? translations?.cart_order_summary_item_count_single_label
      : translations?.cart_order_summary_item_count_multiple_label;

  if (!label) {
    return "";
  }

  return `${count} ${label}`;
};

/**
 * Checks if a URL starts with `https://cdn.sanity.io`.
 *
 * @param {string} url - The URL to check.
 * @returns {boolean} - Returns `true` if the URL starts with `https://cdn.sanity.io`, otherwise `false`.
 */
export function isSanityImageURL(url: string): boolean {
  return url?.startsWith("https://cdn.sanity.io");
}

export function isStaticImage(path: string): boolean {
  return typeof path === "string" && path?.startsWith("/magrabi/images/");
}

/**
 * This function is used to construct sanity link with locale
 *
 * @param {string} link
 * @param {string} locale
 * @return {*}  {string}
 */
export const constructSanityInternalLink = (link: string, locale: string): string => {
  if (
    i18n.locales?.includes(link?.split("/")?.[0]) ||
    i18n.locales?.includes(link?.split("/")?.[1])
  ) {
    const invalidLocale = link?.split("/")?.[0] || link?.split("/")?.[1];
    return link.replace(`${invalidLocale}`, locale);
  }
  if (link?.startsWith("/")) {
    return `/${locale}${link}`;
  }
  return link ? `/${locale}/${link}` : "";
};

/**
 * This function is used to get recommendation models
 *
 * @param {Recommendation} recommendations
 * @return {[]}
 */
export const getRecommendationModels = (recommendations: Recommendation): RecommendModels[] => {
  let recommendModel: RecommendModels[] = [];
  if (hasArrayElements(recommendations?.types)) {
    recommendModel = recommendations?.types?.reduce(
      (accumulator: RecommendModels[], current: { model: RecommendModels }) => {
        if (current.model !== null) {
          accumulator.push(current?.model);
        }
        return accumulator;
      },
      [],
    );
  }
  return recommendModel;
};

export const getSlugs = (recommendation: Recommendation): { [key: string]: string[] } => {
  const slugObject: { [key: string]: string[] } = {};
  if (!recommendation || !recommendation?.types) return slugObject;
  recommendation.types.forEach((item: RecommendationType) => {
    let slugs: string[] = [];
    switch (item?.type) {
      case recommendationType.SALEOR_COLLECTION:
        if (hasArrayElements(item.collections)) {
          slugs = item.collections.map((value) => value.slug);
        }
        break;
      case recommendationType.CUSTOM:
      case recommendationType.CUSTOM_BASE:
      case recommendationType.CUSTOM_INTERMEDIATE:
        if (hasArrayElements(item.products)) {
          item.products.forEach((value) => {
            if (value?.sku) {
              slugs.push(value?.sku);
            }
          });
        }
        break;
    }
    if (hasArrayElements(slugs)) {
      slugObject[item.type] = slugs;
    }
  });
  return slugObject;
};

export const extractRecommendationTypes = (types: RecommendationType[]): string[] => {
  const typeValues: string[] = [];
  if (hasArrayElements(types)) {
    types?.forEach((item: RecommendationType) => {
      typeValues.push(item?.type);
    });
  }
  return typeValues;
};

export const getReorderProduct = (products: RecommendProductItem[], slugs: string[]) => {
  let reOrderRecommendationProduct: RecommendProductItem[] = [];
  if (hasArrayElements(products) && hasArrayElements(slugs)) {
    slugs.forEach((slug) => {
      const recommendProductItem = products.find((product) => product.slug === slug);
      if (recommendProductItem) {
        reOrderRecommendationProduct.push(recommendProductItem);
      }
    });
  }
  return reOrderRecommendationProduct;
};

export const removeDuplicatesByproductId = (recommendationProducts: RecommendProductItem[]) => {
  const uniqueIds: { [productId: string]: boolean } = {};
  const result: RecommendProductItem[] = [];
  recommendationProducts?.forEach((item: RecommendProductItem) => {
    if (item?.productId && !uniqueIds[item.productId]) {
      uniqueIds[item?.productId] = true;
      result.push(item);
    }
  });
  return result;
};

export const constructUrl = (link: LinkContent | undefined, locale: string) =>
  link?.type === "external" ? link?.url : constructSanityInternalLink(link?.url ?? "", locale);

/**
 * This method is used to identify the environment for checkout.com
 *
 * @param {string} secretKeyOrPublishableKey
 * @return {*}
 */
export const getEnvironmentFromKey = (secretKeyOrPublishableKey: string) => {
  return secretKeyOrPublishableKey.startsWith("sk_sbox_") ||
    secretKeyOrPublishableKey.startsWith("pk_sbox_")
    ? paymentConstants.TEST
    : paymentConstants.LIVE;
};

/**
 * This method is used to get Checkout.com api host.
 *
 * @param {string} key
 * @return {*}
 */
export const getCheckoutApiHost = (key: string) => {
  const environment = getEnvironmentFromKey(key);
  if (environment === "test") {
    return "https://api.sandbox.checkout.com";
  } else {
    return "https://api.checkout.com";
  }
};


/**
 *This method is used to get the currency code associated with a locale.
 *
 * @param {string} locale
 * @return {*}
 */

export const getCurrencyCode = (locale: string) => {
  let currency: string = "";
  if (locale) {
    // const currencyMappings = JSON.parse(process.env.CURRENCY_MAPPING ?? "");

    currency = currencyMappings?.[locale] ?? "";
  }
  return currency;
};

export const getCurrencyByLocale = (locale: string): string => {
  if (!locale) return "";

  const language: string = getLangFromLocale(locale);

  // const currencyMappings = JSON.parse(process.env.CURRENCY_MAPPING ?? "");
  const currency =
    (currencyMappings?.[locale as keyof typeof currencyMappings] as any)?.toUpperCase() ?? "";

  const currencySymbols: Record<string, { [key: string]: string }> = {
    AED: { en: "AED", ar: "د.إ" },
    SAR: { en: "SAR", ar: "ر.س" },
    KWD: { en: "KWD", ar: "د.ك" },
    QAR: { en: "QAR", ar: "ر.ق" },
    EGP: { en: "EGP", ar: "ج. م" },
  };

  // console.log("currency", currency, "locale", locale, "language", language);

  return currencySymbols[currency][language] || "";
};

export const currencyFontClassMap: Record<string, { symbol: string; class: string }> = {
  AED: { symbol: "\uE901", class: "dirham-symbol" },
  "د.إ": { symbol: "\uE901", class: "dirham-symbol" },
  SAR: { symbol: "\uE900", class: "riyal-symbol" },
  "ر.س": { symbol: "\uE900", class: "riyal-symbol" },
};

export const getCurrencySymbolByLocale = (locale: string): string => {
  if (!locale) return "";

  const language: string = getLangFromLocale(locale);

  const currency =
    (currencyMappings?.[locale as keyof typeof currencyMappings] as any)?.toUpperCase() ?? "";
  return getCurrencySymbol(currency, language);
};

/**
 * This function returns special font class for currency symbols
 * used for font-based glyph rendering
 * @param {string} currency
 * @returns {string}
 */
export const getCurrencyFontClass = (currency: string): string => {
  const normalized = currency?.toUpperCase?.() || currency;
  return currencyFontClassMap[normalized]?.class ?? "";
};

export const CURRENCY_REGEX = /(AED|SAR|د\.إ|ر\.س)/gi;

/**
 * renderCurrencyFontIconsInHTML - Replaces currency codes in a string with HTML spans
 * using the project's custom font-based glyphs.
 * @param {string} text
 * @returns {string}
 */
export const renderCurrencyFontIconsInHTML = (text: string): string => {
  if (!text) return "";

  return text.replace(CURRENCY_REGEX, (match) => {
    const normalized = match.toUpperCase();
    const config = currencyFontClassMap[normalized] || currencyFontClassMap[match];
    if (config) {
      return `<span class="inline-flex items-center justify-center ${config.class}">${config.symbol}</span>`;
    }
    return match;
  });
};

const currencySymbols: Record<string, { [key: string]: string }> = {
  AED: { en: "\uE901", ar: "\uE901" },
  SAR: { en: "\uE900", ar: "\uE900" },
  KWD: { en: "KWD", ar: "د.ك" },
  QAR: { en: "QAR", ar: "ر.ق" },
  EGP: { en: "EGP", ar: "ج. م" },
};

export const getCurrencySymbol = (currency: string, language: string): string => {
  const code = currency?.toUpperCase();
  if (currencySymbols[code] && currencySymbols[code][language]) {
    return currencySymbols[code][language];
  }
  return "";
};

/**
 * Replaces the currency placeholder in an HTML string with a span containing the correct font class and symbol.
 * Supports {{CURRENCY_ICON}} (uses locale default) and {{CURRENCY_ICON_CODE}} (e.g. {{CURRENCY_ICON_SAR}}).
 *
 * @param html The input HTML string.
 * @param locale The current locale (e.g. "en-ae", "ar-sa") to derive symbol and font class.
 * @returns The HMTL string with the placeholder replaced.
 */
export const replaceCurrencyPlaceholderInHtml = (html: string, locale: string): string => {
  const regex = /{{\s*CURRENCY_ICON(?:_([A-Z]{3}))?\s*}}/gi;
  if (!html || !html.match(regex)) {
    return html;
  }

  const defaultCurrencyCode = getCurrencyCode(locale);
  const language = getLangFromLocale(locale);

  return html.replace(regex, (match, capturedCode) => {
    const code = (capturedCode || defaultCurrencyCode)?.toUpperCase();
    const fontClass = getCurrencyFontClass(code);
    const symbol = getCurrencySymbol(code, language);

    return `<span class="${fontClass}">${symbol}</span>`;
  });
};

/**
 * This function updates the locale in a URL.
 *
 * @param {string} locale
 * @param {string} pathname
 * @returns {string}
 */
export const updateLocaleInPathname = (locale: string, pathname: string): string => {
  return locale && pathname ? pathname.replace(/^\/[a-z]{2}-[a-z]{2}/, locale) : "";
};

export const formatDate = (d: string) => {
  const date = new Date(d);
  return date.toLocaleDateString();
};

export const localAsset = (imageUrl: string) => {
  return imageUrl?.startsWith("http") ? imageUrl : `/magrabi/${imageUrl}`;
};

export const capitalizeFirstLetter = (str: string): string => {
  if (str && typeof str === "string") {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  return str;
};

export const productLinkConstructor = (
  landingPageBaseUrl?: string,
  slug?: string,
  id?: string,
  source?: string,
  index?: string,
  sourceId?: string,
  target?: string,
  queryID?: string,
) => {
  if (!landingPageBaseUrl && !slug) return { href: "" };
  return {
    href: {
      pathname:
        landingPageBaseUrl || `/product/${slug}${id && !landingPageBaseUrl ? `/${id}` : ""}`,
      query: {
        ...(source ? { source } : {}),
        ...(index ? { index } : {}),
        ...(sourceId ? { sourceId } : {}),
        ...(queryID ? { queryID } : {}),
      },
    },
    ...(target ? { target } : {}),
  };
};

/**
 * This function used to find translated city/Area from the city list.
 *
 * @param {CityType[]} areas
 * @param {string} selectedArea
 * @returns {string}
 */
export const getCityAreaTranslation = (areas: CityType[], selectedArea: string) => {
  const area =
    (hasArrayElements(areas) && areas.find((area) => area?.raw === selectedArea)) ||
    ({} as CityType);
  return area?.verbose;
};

export const getTranslatedAreas = (city: string, cities: CityType[], language: string) => {
  const value = cities?.find((c) => c?.verbose === city || c?.raw === city);
  return language === "en" ? value?.raw : value?.verbose;
};

/**
 * This function used to find translated city/Area from the city list.
 *
 * @param {CityType[]} areas
 * @param {string} selectedArea
 * @returns {string}
 */
export const getCityOrAreaTranslation = (value: string, translations: CityType[], lang: string) => {
  const translation =
    (hasArrayElements(translations) &&
      translations.find((translation) => translation?.raw === value)) ||
    ({} as CityType);
  return translation?.[lang === "en" ? "raw" : "verbose"] ?? value;
};

export const toFixed = (number: number, decimals = 0) => Number(number || 0)?.toFixed(decimals);

export const abs = (number: number) => Math.abs(Number(number || 0));

/**
 * This function is used to generate a random UUIDv4.
 * @returns {string}
 */
export const uuidv4 = () => {
  return (
    "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
      (+c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))).toString(16),
    ) +
    "" +
    Date.now()
  );
};

/**
 * This function generates a UUID-like string using a timestamp.
 * @returns {string}
 */
export const uuidCustom = () => {
  const timestamp = Date.now().toString(16);
  return `10000000-1000-4000-8000-${timestamp?.substring(0, 12)}`;
};

export const constructGoogleMapsDirectionUrl = (
  destinationCoordinates: StoreLocation,
  originCoordinates?: GeoLocation,
) => {
  const destination = `${destinationCoordinates.latitude},${destinationCoordinates.longitude}`;
  const origin = originCoordinates
    ? `&origin=${originCoordinates.lat},${originCoordinates.lng}`
    : "";

  return {
    href: `https://www.google.com/maps/dir/?api=1${origin}&destination=${destination}`,
    target: "_blank",
  };
};

export const constructGoogleMapsDirectionUrlByStoreName = (
  destination: string,
  originCoordinates?: GeoLocation,
  destinationCoordinates?: GeoLocation,
) => {
  const origin = originCoordinates
    ? `&origin=${originCoordinates.lat},${originCoordinates.lng}`
    : "";

  // If destination coordinates are provided, use them; otherwise use the destination string
  let destinationParam = "";
  if (destinationCoordinates) {
    destinationParam = `&destination=${destinationCoordinates.lat},${destinationCoordinates.lng}`;
  } else {
    const encodedDestination = encodeURIComponent(destination);
    destinationParam = `&destination=${encodedDestination}`;
  }

  return {
    href: `https://www.google.com/maps/dir/?api=1${origin}${destinationParam}`,
    target: "_blank",
  };
};

export const getStoreAddressString = (storeAddress: StoreAddress): string => {
  const { streetAddress1, streetAddress2, city, cityArea, country } = storeAddress || {};
  const addressComponents = [
    streetAddress1,
    streetAddress2,
    city,
    cityArea,
    country?.country,
  ].filter(Boolean);
  return addressComponents.join(", ");
};

export const getColorCombinationFromKey = (key: string): string => {
  return key
    .split("-")
    .map((key) => key.charAt(0).toUpperCase() + key.slice(1))
    .join(" / ");
};

/**
 * This method is used to parse url to youTube id
 * for construct thumb image of youTube video
 * @param {string} url
 * @returns
 */
export const youtubeIdExtractor = (url?: string): string => {
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url?.match(regExp) || null;
  return match && match[7].length === 11 ? match[7] : "";
};

/**
 * This method is used to check wether url is youTube or not.
 * @param {String} url
 * @returns
 */
export const isYoutubeUrl = (url: string): boolean => {
  const pattern =
    /^(?:https?:\/\/)?(?:m\.|www\.)?(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})(?:\S+)?$/;
  return pattern.test(url);
};

/**
 * This method is used to construct youTube thumb image
 * @param {string} youtubeId
 * @returns
 */
export const constructPreviewImage = (youtubeId: string = "", type: string = "default"): string => {
  return youtubeId
    ? `https://img.youtube.com/vi/${youtubeId}/${type}.jpg`
    : "/magrabi/images/no-image-square.svg";
};

/**
 * This method is used to construct youTube embed url
 * @param {string} youtubeId
 * @returns
 */
export const constructYoutubeEmbedUrl = (youtubeId: string): string =>
  `https://www.youtube.com/embed/${youtubeId}`;

/**
 * This method is used to construct sorted and group brands with first character
 * @param {BrandNode} node
 * @param {string} type
 * @param {string} language
 * @returns
 */
export const getTranslatedName = (node: BrandNode, type: string, language: string): string => {
  if (type == ourBrandsFields.NAME) {
    return getValueBasedOnLanguage(language, node, "name", "name", node?.translation) || "";
  }
  if (type == ourBrandsFields.BADGE) {
    const { availableBadge } = node || {};
    return (
      getValueBasedOnLanguage(
        language,
        availableBadge,
        "name",
        "name",
        availableBadge?.translation,
      ) || ""
    );
  }
  return "";
};

/**
 * This method is used to construct sorted and group brands with first character
 * @param {BrandEdges[]} brandEdges
 * @param {string} language
 * @returns
 */
export const getGroupedBrandsWithFirstChar = (
  brandEdges: BrandEdges[] = [],
  language: string,
): GroupedBrands => {
  const sortedGroupedBrands: GroupedBrands = {};

  if (hasArrayElements(brandEdges)) {
    const groupedBrands: GroupedBrands = {};
    const unGrouped: GroupedBrands = {};

    brandEdges.forEach((brandEdge) => {
      const name = getTranslatedName(brandEdge?.node, ourBrandsFields.NAME, language);
      // const numberKey = language === "en" ? "0-9" : "9-0";
      if (name && name?.trim()) {
        const trimmedName = name.trim().toUpperCase();
        const letters = language === "en" ? enAlphabets : arAlphabets;
        // const firstChar = trimmedName?.charAt(0).toUpperCase();
        // const groupKey = firstChar?.match(/[0-9]/) ? numberKey : firstChar;
        const idx =
          language === "en"
            ? (trimmedName?.charCodeAt(0) ?? 65) - 65 + 1
            : getArabicLetterIndex(trimmedName);

        // checks if idx -1
        if (idx < 0) {
          const key = trimmedName?.charAt(0);
          if (!unGrouped[key]) {
            unGrouped[key] = [];
          }
          unGrouped[key].push(brandEdge);
        } else {
          const groupKey = letters[idx];

          if (!groupedBrands[groupKey]) {
            groupedBrands[groupKey] = [];
          }
          groupedBrands[groupKey].push(brandEdge);
        }
      }
    });

    const mergedGroups = { ...groupedBrands, ...unGrouped };

    if (Object.keys(mergedGroups) && Object.keys(mergedGroups).length > 0) {
      const sortedKeys = Object.keys(mergedGroups).sort((a, b) => a.localeCompare(b, language));
      sortedKeys.forEach((key) => {
        sortedGroupedBrands[key] = mergedGroups[key];
      });
    }
  }
  return sortedGroupedBrands;
};

/**
 * This method is used to construct sorted and group brands with categoryId
 * @param {BrandEdges[]} brandEdges
 * @returns
 */
export const getGroupedCategories = (brandEdges: BrandEdges[] = []): GroupedCategories => {
  const groupedCategories: GroupedCategories = {
    All: {
      brands: brandEdges,
      name: ourBrandsFields.CATEGORY_ALL,
      nameArabic: ourBrandsFields.CATEGORY_ALL_ARABIC,
    },
  };
  if (hasArrayElements(brandEdges)) {
    brandEdges.forEach((brandEdge) => {
      const categories = brandEdge?.node?.category || [];
      if (hasArrayElements(categories)) {
        categories.forEach((category) => {
          const rootCategory = category?.parent?.slug !== "universal" ? category?.parent : category;
          if (
            rootCategory?.id &&
            rootCategory?.name &&
            BrandMainCategories.includes(rootCategory?.name)
          ) {
            if (!groupedCategories?.[rootCategory?.id]) {
              groupedCategories[rootCategory?.id] = {
                brands: [],
                name: rootCategory?.name,
                nameArabic: rootCategory?.translation?.name || rootCategory?.name,
              };
            }
            // Add brandEdge to Set, for handle duplicates while grouped with parent category
            const brandList = new Set(groupedCategories?.[rootCategory?.id]?.brands ?? []);
            brandList.add(brandEdge);
            groupedCategories[rootCategory?.id].brands = Array.from(brandList);
          }
        });
      }
    });
  }
  return groupedCategories;
};

export const formatDateToMMYYY = (dateString: string) => {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "";
  }
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${monthMap[month]} ${year}`;
};

export const formatDateToDDMMYYYY = (dateString: string) => {
  if (!dateString) {
    return "";
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return "";
  }
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * This method is used to convert text to capitalized sentence case with space E.g: "hello_world" => "Hello World"
 * @param {string} text text to convert
 * @returns {string} converted text
 */
export const convertToSentenceCase = (text: string) => {
  const words = text.split("_");
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");
};

export const isVtoEnabled = (variant: VariantItem) => {
  return variant?.attributes?.[vtoConstants.attirbute] === vtoConstants.value;
};

export const formatPhoneNumber = (
  countryCode: string,
  phoneNumber: string,
  useThreeDigitFormat: boolean = true,
  checkByDigits?: boolean,
): string => {
  const country = addressCountryMappings[countryCode];
  if (!country) {
    throw new Error(`Invalid country code: ${countryCode}`);
  }

  let cleanNumber = phoneNumber?.replace(/^\+/, "");
  if (cleanNumber?.startsWith(country?.dialCode?.slice(1))) {
    cleanNumber = cleanNumber.slice(country.dialCode.length - 1);
  }

  const digits = cleanNumber?.replace(/\D/g, "");

  if (checkByDigits) {
    useThreeDigitFormat = digits.length > 9;
  }
  const format: string = useThreeDigitFormat
    ? country.phoneNumberPattern.FORMAT_3_DIGIT
    : country.phoneNumberPattern.FORMAT_2_DIGIT;

  let formattedNumber = "";
  let digitIndex = 0;

  for (let i = 0; i < format?.length && digitIndex < digits?.length; i++) {
    if (format[i] === "9") {
      formattedNumber += digits[digitIndex];
      digitIndex++;
    } else {
      formattedNumber += format[i];
    }
  }

  formattedNumber = formattedNumber?.replace(/\D+$/, "");

  return `${country.dialCode} ${formattedNumber}`;
};

/**
 * Formats a phone number value according to the provided format pattern
 * @param phone - The phone number to format (can include non-digit characters)
 * @param format - The format pattern (e.g., "99-999-9999" where 9 represents a digit)
 * @returns Formatted phone number with dashes in the correct positions
 */
export const formatPhoneValueWithMask = (phone: string, format: string): string => {
  if (!phone) return "";
  const cleanPhone = phone.replace(/\D/g, "");

  let formatted = "";
  let phoneIndex = 0;

  for (let i = 0; i < format.length && phoneIndex < cleanPhone.length; i++) {
    if (format[i] === "9") {
      formatted += cleanPhone[phoneIndex];
      phoneIndex++;
    } else {
      formatted += format[i];
    }
  }

  return formatted;
};

export const isStringTrue = (value: unknown): boolean =>
  typeof value === "string" ? value === "true" : Boolean(value);

export const isRedirectDisabled = (searchParams: ReadonlyURLSearchParams) => {
  return isStringTrue(searchParams.get(sessionQueryParams.DISABLE_REDIRECT));
};

export const getChannelId = (locale: string) => {
  const channel =
    process.env.SYSTEM_BRAND_ID || process.env.NEXT_PUBLIC_BRAND_ID
      ? `${process.env.SYSTEM_BRAND_ID || process.env.NEXT_PUBLIC_BRAND_ID}-${getCountryCodeFromLocale(locale)}-web`
      : "";
  return channel;
};

export const getCurrentDateByTimeZone = (locale: string): string => {
  let timeZone = "";
  switch (locale) {
    case "ae-en":
    case "ae-ar":
      timeZone = "Asia/Dubai";
    case "sa-en":
    case "sa-ar":
      timeZone = "Asia/Riyadh";
    case "eg-en":
    case "eg-ar":
      timeZone = "Africa/Cairo";
    case "qa-en":
    case "qa-ar":
      timeZone = "Asia/Qatar";
    case "kw-en":
    case "kw-ar":
      timeZone = "Asia/Kuwait";
    default:
      timeZone = "";
  }
  return new Date().toLocaleString("en-US", { timeZone: timeZone || "Asia/Dubai" });
};

export const getCurrentTimeOfDay = (currentDate: string): string => {
  const now = new Date(currentDate);
  const hours = now.getHours();

  if (hours >= 5 && hours < 12) {
    return "Morning";
  } else if (hours >= 12 && hours < 17) {
    return "Afternoon";
  } else if (hours >= 17 && hours < 21) {
    return "Evening";
  } else {
    return "Night";
  }
};

export function looksSerializable(obj: unknown): boolean {
  return (
    obj === null ||
    typeof obj === "boolean" ||
    typeof obj === "number" ||
    typeof obj === "string" ||
    Array.isArray(obj) ||
    (typeof obj === "object" && obj.constructor === Object)
  );
}

export function getOperationName(gqlQueryString: string | null) {
  const match = gqlQueryString && gqlQueryString.match(/\b(query|mutation)\s+([a-zA-Z0-9_]+)/i);

  if (match) {
    const operationType = match[1].toLowerCase();
    const operationName = match[2];
    return `${operationType} ${operationName}`;
  }

  return "<unknown>";
}

export const getCurrentDayType = (currentDate: string): string => {
  const now = new Date(currentDate);
  const day = now.getDay();

  return day >= 1 && day <= 5 ? "Weekday" : "Weekend";
};

export const createGaUserObject = (user?: TypeSessionUser): GAUserObject => {
  return {
    loggedIn: user?.email ? "Yes" : "No",
    magrabiID: user?.magrabi_id ?? "",
    userType: user?.email ? "User" : "Anonymous",
    subscribed_user: isStringTrue(user?.promotionsSubscribed) ? "Y" : "N",
    fullName: user?.name ?? "",
    firstName: user?.given_name ?? "",
    lastName: user?.family_name ?? "",
    phoneNumber: user?.phone_number ?? "",
    userEmail: user?.email || user?.anonymousUserEmail || "",
    hashedEmail:
      user?.email || user?.anonymousUserEmail
        ? generateHash(user?.email || user?.anonymousUserEmail || "")
        : "",
  };
};

export const createGaInitialObject = (
  locale: string,
  user?: TypeSessionUser,
  gaPageInfo?: GaPageInfo,
): GAInitialObject => {
  const userData: GAUserObject = createGaUserObject(user);
  const currentDate = getCurrentDateByTimeZone(locale);
  return {
    ...userData,
    pageType: gaPageInfo?.pageType ?? "", // Home Page / PLP / PDP
    pageCategory: gaPageInfo?.pageCategory ?? "", // "Sunglasses", "Eyeglasses", "Contact Lenses Clear", "Contact Lenses Color", "Accessories By Color"
    storeLanguage: getLangFromLocale(locale),
    storeCurrency: getCurrencyCode(locale) ?? "",
    storeCountry: getCountryCodeFromLocale(locale) ?? "",
    storeID: gaPageInfo?.storeId ?? "", // If any store id
    day_time: getCurrentTimeOfDay(currentDate) ?? "",
    day_weekday: getCurrentDayType(currentDate) ?? "",
    pageUrl: gaPageInfo?.currentPageUrl ?? "",
    previousUrl: gaPageInfo?.previousPageUrl ?? "",
    pageTitle: gaPageInfo?.pageTitle ?? "",
  };
};

export const createUserJourneyAnalyticsPayload = (journey: string, user: TypeSessionUser) => {
  const userData: GAUserObject = createGaUserObject(user);
  switch (journey) {
    case sessionFlowConstants.LOGIN_EMAIL:
      return {
        eventType: analyticEventTypes.CUSTOM,
        data: {
          target: analyticEvents.LOGIN,
          method: "email",
          userData,
        },
      };

    case sessionFlowConstants.LOGIN_PHONE:
      return {
        eventType: analyticEventTypes.CUSTOM,
        data: {
          target: analyticEvents.LOGIN,
          method: "phone",
          userData,
        },
      };

    case sessionFlowConstants.REGISTER:
      return {
        eventType: analyticEventTypes.CUSTOM,
        data: {
          target: analyticEvents.SIGN_UP,
          method: "email",
          userData,
        },
      };

    default:
      return null;
  }
};

export const getAssetFileNameFromUrl = (url?: string): string => url?.split("/")?.pop() ?? "";
export const isImageUrl = (url: string): boolean => {
  return url?.startsWith("http");
};

export const getErrorMessage = (error: unknown) => {
  if (error instanceof Error) return error.message;
  return String(error);
};

export const getReturnWindow = (deliveredDate: string) => {
  const deliveredDateObj = new Date(deliveredDate);
  deliveredDateObj.setHours(23, 59, 59, 999);
  const now = new Date();
  const timeReamining = deliveredDateObj.getTime() + 14 * 24 * 60 * 60 * 1000 - now.getTime();
  return {
    isReturnable: timeReamining > 0,
    daysRemaining: Math.floor(timeReamining / (24 * 60 * 60 * 1000)),
  };
};

export const isOrderInReturnWindow = (deliveredDate: string) => {
  const deliveredDateObj = new Date(deliveredDate);
  deliveredDateObj.setHours(23, 59, 59, 999);
  const now = new Date();
  return deliveredDateObj.getTime() + 14 * 24 * 60 * 60 * 1000 > now.getTime();
};
export const getTamaraConfiguredCountries = () => {
  const countries = process.env.NEXT_PUBLIC_TAMARA_COUNTRIES?.split(",");
  const tamaraConfiguredCountries: string[] = [];
  if (countries) {
    countries.forEach((eachCountry) => {
      const country = (eachCountry && `${eachCountry}`.trim()) || null;
      if (country) {
        tamaraConfiguredCountries.push(country);
      }
    });
  }
  return tamaraConfiguredCountries;
};

export const getTabbyConfiguredCountries = () => {
  const countries = process.env.NEXT_PUBLIC_TABBY_COUNTRIES?.split(",");
  const tabbyConfiguredCountries: string[] = [];
  if (countries) {
    countries.forEach((eachCountry) => {
      const country = (eachCountry && `${eachCountry}`.trim()) || null;
      if (country) {
        tabbyConfiguredCountries.push(country);
      }
    });
  }
  return tabbyConfiguredCountries;
};

export const getTabbyMerchantCode = (country: string) => {
  if (country === "ae") {
    return process.env.NEXT_PUBLIC_TABBY_UAE_MERCHANT_KEY;
  } else {
    return process.env.NEXT_PUBLIC_TABBY_KSA_MERCHANT_KEY;
  }
};

export const getTamaraScriptSource = () => {
  return process.env.NEXT_PUBLIC_TAMARA_SCRIPT_SRC;
};

export const getPercentHideCountries = () => {
  // TODO(QA): When Qatar (qa) is enabled for DoctorM, wire this into layout/RootProvider as displayPercentage
  // (same as Magrabi) and gate all product % badges (PLP/PDP/cart/LCP) with it.
  const countries = process.env.NEXT_PUBLIC_PERCENTAGE_SYMBOL_HIDE_COUNTRIES?.split(",");
  const percentaHideCountries: string[] = [];
  if (countries) {
    countries?.forEach((eachCountry) => {
      const country = (eachCountry && `${eachCountry}`.trim()) || null;
      if (country) {
        percentaHideCountries.push(country);
      }
    });
  }
  return percentaHideCountries;
};

export const getMagrabiLocationIcon = (isSelected?: boolean, index: number = 0) => {
  const fillColor = isSelected ? "%23EC5D3C" : "%23000000";
  return {
    url: `data:image/svg+xml;utf8,
    <svg xmlns="http://www.w3.org/2000/svg" width="26" height="32" viewBox="0 0 26 32" fill="none">
      <path d="M26 13.0377C26 20.2806 19.5 24.5 13 32C6.5 24.5 0 20.2806 0 13.0377C0 5.79481 5.82179 0 13 0C20.1782 0 26 5.79481 26 13.0377Z" fill="${fillColor}"/>
      <text x="13" y="18" text-anchor="middle" alignment-baseline="middle" font-size="12" font-weight="bold" fill="white">${index + 1}</text>
    </svg>`,
  };
};

export const getCurrentLocationIcon = () => ({
  url: `data:image/svg+xml;utf8,
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="16" r="16" fill="%234285F4" fill-opacity="0.5"/>
          <circle cx="16" cy="16" r="10" fill="%234285F4"/>
          <circle cx="16" cy="16" r="4" fill="white"/>
        </svg>`,
});

export const constructBookEyeTestUrl = (storeId: string, serviceId?: number, locale?: string) => {
  const prefix = locale ? `/${locale}` : "";
  // If serviceId is provided and valid, go to date selection
  if (serviceId && serviceId > 0) {
    return {
      href: `${prefix}/book-eyetest/date?store=${storeId}&serviceId=${serviceId}`,
    };
  }
  // If no serviceId, go to type selection (old flow)
  return {
    href: `${prefix}/book-eyetest/type?store=${storeId}`,
  };
};

export const isValidUser = (user: unknown) => !!(user as TypeSessionUser)?.email;

export const getValuConfiguredCountries = () => {
  const countries = process.env.NEXT_PUBLIC_VALU_COUNTRIES?.split(",");
  const valuConfiguredCountries: string[] = [];
  if (countries) {
    countries?.forEach?.((eachCountry) => {
      const country = (eachCountry && `${eachCountry}`.trim()) || null;
      if (country) {
        valuConfiguredCountries.push(country);
      }
    });
  }
  return valuConfiguredCountries;
};

export const updateDateSeperator = (date: string, seperator: string) => {
  return date.replaceAll(/\//g, seperator);
};

export const isAllowedFormat = (type: string) => {
  return Object.values(allowedPrescriptionFileTypes).includes(type);
};

export const isHeic = (name: string) => {
  return !!heifFileTypes?.[name?.split(".")?.pop() || ""];
};

export const isPDF = (url: string) => {
  return url?.endsWith(".pdf") || url?.endsWith(".PDF");
};

export const generateHash = (value: string): string => {
  const hash = crypto.createHash("sha256");
  hash.update(value);
  return hash.digest("hex");
};

export function encode(value: string) {
  return Buffer.from(value, "utf-8").toString("base64");
}

export function decode(value: string) {
  return Buffer.from(value, "base64").toString("utf-8");
}

export const getCurrencyBasedOnLanguage = (currency: string): string => {
  if (currency) {
    switch (currency.toUpperCase()) {
      case "AED":
        return "د.إ";
      case "SAR":
        return "ر.س";
      case "KWD":
        return "د.ك";
      case "QAR":
        return "ر.ق";
      case "EGP":
        return "ج. م";
      default:
        return currency;
    }
  }
  return currency;
};

export const isContactLensProduct = (productType?: string): boolean =>
  (productType &&
    [ProductType.CL_CLEAR.toString(), ProductType.CL_COLOR.toString()].includes(productType)) ||
  false;

export const hasLensPower = (productType?: string): boolean =>
  !!productType &&
  [ProductType.ACC_BY_COLOR_BY_POWER, ProductType.CL_CLEAR, ProductType.CL_COLOR].includes(
    productType,
  );

export function isCardExpired(expMonth: number, expYear: number): boolean {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();
  if (expMonth && expYear) {
    if (expYear < currentYear) {
      return true;
    }
    if (expYear === currentYear && expMonth < currentMonth) {
      return true;
    }
  }
  return false;
}
export const parseCustomerNote = (note?: string, key?: string): Record<string, any>[] => {
  const parsedNote = note ? JSON.parse(note) : {};
  return key ? parsedNote[key] || [] : parsedNote;
};

export const constructEyeTestStoreUrl = (
  serviceId: number, // Change from type string to serviceId number
  storeId?: string,
  isDisabled?: boolean,
  locale?: string,
) => {
  const prefix = locale ? `/${locale}` : "";
  if (storeId && !isDisabled) {
    return {
      href: `${prefix}/book-eyetest/date?store=${storeId}&serviceId=${serviceId}`,
    };
  }
  return {
    href: `${prefix}/book-eyetest/store?serviceId=${serviceId}`,
  };
};

export const scrollToElementWithOffsets = (elementId: string, idsToCheck: string[] = []) => {
  if (typeof window === "undefined") {
    return;
  }
  const element = document.getElementById(elementId);
  if (element) {
    const totalOffset = idsToCheck.reduce((total, id) => {
      const el = document.getElementById(id);
      return total + (el ? el.offsetHeight : 0);
    }, 0);
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({
      top: elementPosition - totalOffset,
      behavior: "smooth",
    });
  }
};

export const constructEyeTestMyInfoUrl = (
  storeId: string,
  date: string,
  timeSlot: EyeTestTimeSlot,
  type?: string,
  bookingId?: string,
  serviceId?: number, // Add serviceId parameter
  locale?: string,
): string => {
  const params = new URLSearchParams({
    store: storeId,
    date,
    slotStart: timeSlot.start,
    slotEnd: timeSlot.end,
  });

  if (bookingId) {
    params.set("id", bookingId);
  } else if (type) {
    params.set("type", type);
  }

  // Add serviceId if provided
  if (serviceId) {
    params.set("serviceId", serviceId.toString());
  }

  const prefix = locale ? `/${locale}` : "";
  return `${prefix}/book-eyetest/my-info?${params.toString()}`;
};

export const getHolidays = (data?: WorkCalendarType): Date[] | undefined => {
  if (!data) return undefined;

  return Object.entries(data)
    .filter(([, day]) => day?.isDayOff)
    .map(([date]) => new Date(date));
};

export const getFirstWorkingDay = (currentDate: Date, holidays?: Date[]): Date => {
  const isSameDay = (dateA: Date, dateB: Date): boolean => {
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  };

  let workingDate = new Date(currentDate);

  if (Array.isArray(holidays)) {
    while (holidays.some((holiday) => isSameDay(holiday, workingDate))) {
      workingDate.setDate(workingDate.getDate() + 1);
    }
  }

  return workingDate;
};

export const formatDateString = (
  dateInput: string | Date | null,
  format: string = dateFormats.DEFAULT_DATEFORMAT,
  locale: string = "default",
): string => {
  if (!dateInput) return "";
  const date = new Date(dateInput);

  switch (format) {
    //DATE FORMATS
    case dateFormats.YYYY_MM_DD:
      return `${date.toLocaleDateString(locale, { year: "numeric" })}-${date.toLocaleDateString(
        locale,
        {
          month: "2-digit",
        },
      )}-${date.toLocaleDateString(locale, { day: "2-digit" })}`;
    case dateFormats.DD_MM_YYYY:
      return `${date.toLocaleDateString(locale, { day: "2-digit" })}-${date.toLocaleDateString(
        locale,
        {
          month: "2-digit",
        },
      )}-${date.toLocaleDateString(locale, { year: "numeric" })}`;
    case dateFormats.DD_MMMM_YYYY:
      return `${date.toLocaleDateString(locale, { day: "2-digit" })}, ${date.toLocaleDateString(
        locale,
        {
          month: "long",
        },
      )}, ${date.toLocaleDateString(locale, { year: "numeric" })}`;
    case dateFormats.MMMM_DD_YYYY:
      return `${date.toLocaleDateString(locale, { month: "long" })} ${date.toLocaleDateString(
        locale,
        {
          day: "2-digit",
        },
      )}, ${date.toLocaleDateString(locale, { year: "numeric" })}`;
    case dateFormats.DD_PFX_MMMM_YYYY:
      return generateDateWithPrefix(date);
    //TIME FORMATS
    case dateFormats.HH_MM:
      return `${date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false })}`;
    case dateFormats.HH_MM_SS:
      return `${date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true })}`;

    //DEFAULT
    default:
      return `${date.toLocaleDateString(locale, { day: "2-digit" })}/${date.toLocaleDateString(
        locale,
        {
          month: "2-digit",
        },
      )}/${date.toLocaleDateString(locale, { year: "numeric" })}`;
  }
};

const generateDateWithPrefix = (date: Date) => {
  const day: number = date.getUTCDate();
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getUTCFullYear();

  // Add the correct suffix to the day
  const daySuffix = (day: number) => {
    if (day > 3 && day < 21) return "th"; // Special case for 11th to 19th
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${day}${daySuffix(day)} ${month} ${year}`;
};

export const constructCalendarLink = (
  type: "google" | "outlook" | "yahoo",
  event: CalendarEventDetails,
) => {
  if (!event.date || !event.startTime || !event.endTime) {
    return "";
  }

  const formattedDate = event.date.replace(/-/g, "");
  const formattedStartTime = event.startTime.replace(/:/g, "");
  const formattedEndTime = event.endTime.replace(/:/g, "");

  // Ensure the correct format for each calendar
  const formattedStart = `${formattedDate}T${formattedStartTime}00`;
  const formattedEnd = `${formattedDate}T${formattedEndTime}00`;

  switch (type) {
    case "google":
      return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title || "")}&dates=${formattedStart}/${formattedEnd}&location=${encodeURIComponent(event.location || "")}`;
    case "outlook":
      return `https://outlook.office.com/owa/?path=/calendar/action/compose&rru=addevent&subject=${encodeURIComponent(event.title || "")}&startdt=${event.date}T${event.startTime}%3A00&enddt=${event.date}T${event.endTime}%3A00&location=${encodeURIComponent(event.location || "")}`;
    case "yahoo":
      return `https://calendar.yahoo.com/?v=60&view=d&type=20&title=${encodeURIComponent(event.title || "")}&st=${formattedStart}&et=${formattedEnd}&in_loc=${encodeURIComponent(event.location || "")}`;
    default:
      return "";
  }
};

export const translateProperties = (
  obj: Record<string, any>,
  translations: Record<string, string | undefined>,
  keys?: string[],
) => {
  const translatableKeys = keys || Object.keys(obj);

  translatableKeys?.forEach((key) => {
    if (obj?.[key]) {
      obj[key] = translations?.[key] || obj?.[key];
    }
  });
  return obj;
};

export const convertTimeString = (time?: string): string => {
  if (!time) return "";

  const [hours, minutes] = time.split(":");
  return `${hours}:${minutes}`;
};

export const getEyeTestServiceByType = (
  eyeTestType?: string,
  myAccountContent?: Record<string, string>,
): string => {
  switch (eyeTestType) {
    case eyeTestServiceType.EYE_TEST:
      return myAccountContent?.my_account_eye_test_service_type_eye_test || "General Eye Test";
    case eyeTestServiceType.RTA_EYE_TEST:
      return (
        myAccountContent?.my_account_eye_test_service_type_rta_eye_test ||
        "RTA Driving License Eye Test"
      );
    case eyeTestServiceType.CONTACT_LENS_FITTING:
      return (
        myAccountContent?.my_account_eye_test_contact_lens_fitting ||
        "Contact Lenses Consultation & Fitting"
      );
    default:
      return eyeTestType || "";
  }
};

export const getEyeTestReservationStatus = (
  status?: string,
  myAccountContent?: Record<string, string>,
  eyeTestType?: string,
) => {
  const testType = getEyeTestServiceByType(eyeTestType, myAccountContent);

  // Check for Pending (English or Arabic)
  if (status === bookingStatus.PENDING || status === bookingStatusArabic.PENDING) {
    return { backgroundColor: "#016FD0", status: status, testType };
  }

  // Check for Completed (English or Arabic)
  if (status === bookingStatus.COMPLETED || status === bookingStatusArabic.COMPLETED) {
    return { backgroundColor: "#34A853", status: status, testType };
  }

  // Check for Cancelled (English or Arabic)
  if (status === bookingStatus.CANCELLED || status === bookingStatusArabic.CANCELLED) {
    return { backgroundColor: "#D92832", status: status, testType };
  }

  // Check for No Show (English or Arabic)
  if (status === bookingStatus.NOSHOW || status === bookingStatusArabic.NOSHOW) {
    return { backgroundColor: "#C4C4C4", status: status, testType };
  }

  // Default case
  return {
    backgroundColor: "#C4C4C4",
    status: status || myAccountContent?.my_account_eye_test_reservation_status_past || "PAST",
    testType,
  };
};

export const getDecodedBookingId = (bookingId: string) => {
  const decodedString = decode(bookingId);
  return decodedString?.split(":")?.[1] || bookingId;
};

export const localeParserForSeo = (locale: string) =>
  `${getLangFromLocale(locale)}-${getCountryCodeFromLocale(locale)}`;

export const getFormattedDateForEyeTest = (
  date?: string,
  time?: string,
  myAccountContent?: Record<string, string>,
  locale: string = "default",
): string => {
  try {
    const constructedDate = new Date(`${date}T${time}`);
    const parsedLocale: string = locale ? localeParserForSeo(locale) : locale;
    const formatedDate: string = date
      ? formatDateString(date, dateFormats.MMMM_DD_YYYY, parsedLocale)
      : "";
    const formatedTime: string =
      date && time ? formatDateString(constructedDate, dateFormats.HH_MM_SS, parsedLocale) : "";
    return `${formatedDate} ${myAccountContent?.my_account_eye_test_date_at || "at"} ${formatedTime || ""} ${myAccountContent?.my_account_eye_test_time_zone || "GMT"}`;
  } catch (error) {
    return `${date} ${myAccountContent?.my_account_eye_test_date_at || "at"} ${time} ${myAccountContent?.my_account_eye_test_time_zone || "GMT"}`;
  }
};

export const parseEnglishProperty = (obj: Record<string, any>, propertyEn: string): string => {
  const keys = propertyEn?.split(".");
  let value: Record<string, any> | string = obj;
  if (hasArrayElements(keys)) {
    keys?.forEach((key) => {
      if (typeof value === "object" && value?.[key]) {
        value = value[key];
      } else {
        return value;
      }
    });
  }
  return typeof value === "string" ? value : "";
};

export const getValueBasedOnLanguage = (
  language: string,
  obj: Record<string, any> = {},
  propertyEn: string,
  propertyAr: string,
  translations?: Record<string, any> | null | undefined,
): string => {
  const hasTranslationNeeded = isTranslationNeeded(language);
  if (hasTranslationNeeded && translations?.[propertyAr]) {
    return translations[propertyAr];
  }
  if (hasTranslationNeeded && !isFallBackTranslationNeeded()) {
    return "";
  }
  return parseEnglishProperty(obj, propertyEn);
};

export const isFallBackTranslationNeeded = (): boolean => {
  return hasFallBackTranslation === "true";
  //   return process.env.HAS_FALL_BACK_TRANSLATION === "true";
};

export const isTranslationNeeded = (language: string): boolean => language === "ar";

export const getVariantTitle = (
  variants?: VariantItemPdp[],
  variantId?: string,
  language?: string,
) => {
  const selectedVariant = variantId
    ? variants?.find((item) => item.id === variantId)
    : variants?.[0];
  if (selectedVariant) {
    const variantTitle = getAttributeValue(
      selectedVariant?.attributes,
      SaleorAttributes.TITLE,
      null,
      language,
    );
    return variantTitle;
  }
  return "";
};

/**
 * @function handleNewsLetterSubscriptionAnalytics
 * @description Function to handle newsletter subscription analytics
 * @param user  User object
 * @returns void
 */
export const handleNewsLetterSubscriptionAnalytics = (
  user: TypeSessionUser,
  userJourney: string,
) => {
  if (
    userJourney === sessionFlowConstants.REGISTER &&
    user?.email &&
    isStringTrue(user?.promotionsSubscribed)
  ) {
    handlePostAnalytics({
      eventType: analyticEventTypes.CUSTOM,
      data: {
        target: analyticEvents.NEWSLETTER_SUBSCRIPTION,
        email: user?.email,
      },
    });
  }
};

/**
 * @function constructLocaleFromLangAndCountryCode
 * @description Function to construct locale from lang and country code
 * @param lang  Language code
 * @param countryCode  Country code
 * @returns string
 * @example constructLocaleFromLangAndCountryCode("en", "us") => "en-us"
 */
export const constructLocaleFromLangAndCountryCode = (lang: string, countryCode: string): string =>
  locales.find(
    (locale) =>
      locale.includes(lang?.toLowerCase()) && locale?.includes(countryCode?.toLowerCase()),
  ) || defaultLocale;

export const getCountryName = (countryCode?: string, language?: string): string => {
  if (!countryCode || !addressCountryMappings?.[countryCode]) return "";
  return language === "ar"
    ? addressCountryMappings[countryCode]?.name?.ar
    : addressCountryMappings[countryCode]?.name?.en;
};

export const isClient = () => typeof window !== "undefined";

export function encodeBase64(str: string) {
  try {
    return btoa(str);
  } catch (e) {
    console.error("Encoding failed:", e);
  }
}
export const constructAlgoliaGuestUserTokenSA = async () => {
  const timestamp = new Date().getTime().toString();
  await setInCookieMetadataSA(algoliaGuestToken.ALGOLIA_GUEST_TOKEN, timestamp);
  return encodeBase64(timestamp);
};

export const getAlgoliaGuestUserTokenSA = async () => {
  const tokenFromCookie = await getFromCookieMetadataSA(algoliaGuestToken.ALGOLIA_GUEST_TOKEN);
  return tokenFromCookie ? encodeBase64(tokenFromCookie) : await constructAlgoliaGuestUserTokenSA();
};

export const getAlgoliaAuthenticatedUserToken = (magrabiId?: string) => {
  return magrabiId ? encodeBase64(magrabiId) : undefined;
};

export const appendItemToBreadCrumbs = (
  language: string,
  items: BreadcrumbItem[],
  category: CategoryDetails,
) => {
  if (category) {
    items.push({
      id: category?.id || category?.slug,
      href:
        category?.landingPageBaseUrl ||
        `/search/${productListingPageKey.categoryListing}/${category?.slug}`,
      label: getValueBasedOnLanguage(language, category, "name", "name", category?.translation),
    });
  }
  return items;
};

export const isKnownInValidUrl = (url: string): boolean => {
  return ["/widget/config_iframe.html"].includes(url);
};

export const parseNumberFromAttributeValue = (value?: string): number | null => {
  if (value) {
    const match = value.match(/-?\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : null;
  }
  return null;
};

/**
 * Refines the Cloudinary URL by adjusting the quality and auto format for video content.
 * It checks if the URL contains the correct Cloudinary cloud name and includes the 'upload/' path,
 * then adds the 'q_90' quality and 'f_auto:video' format options.
 *
 * @param {string} url - The Cloudinary URL to refine.
 * @returns {string} - The modified Cloudinary URL with the applied quality and format settings.
 */
export const refineCloudinaryUrlQuality = (url: string): string => {
  if (url?.includes("/video/upload")) {
    return url.replace("/video/upload/", `/video/upload/q_auto,f_auto:video/`);
  }
  return url;
};

/**
 * Generate Poster image from a valid cloudinary video URL
 *
 * @param {string} url - The Cloudinary video URL to modify.
 * @returns {string} - The modified URL with added transformations or the original URL if it's not a valid Cloudinary video URL.
 */
export const getCloudinaryVideoPoster = (url: string): string => {
  if (url?.includes("/video/upload")) {
    return url.replace("/video/upload/", "/video/upload/f_auto:image,q_auto/");
  }
  return url;
};

export function areObjectsDifferent(
  obj1?: { [key: string]: string | null } | null,
  obj2?: { [key: string]: string | null },
): boolean {
  // If both are null or undefined, they are considered the same
  if (!obj1 && !obj2) return false;

  const keys1 = Object.keys(obj1 || {});
  const keys2 = Object.keys(obj2 || {});

  // If the number of keys are different, they are different
  if (keys1.length !== keys2.length) return true;

  // Special case: if one object is null/undefined but the other has keys
  if (!obj1 && keys2.length > 0) return true;
  if (!obj2 && keys1.length > 0) return true;

  // Check if any key-value pair differs
  return !!(obj1 && obj2 && keys1.some((key) => obj1[key] !== obj2[key]));
}

export function formatDateToMonthYear(input: string, language: string = "en") {
  const date = new Date(input);
  if (isNaN(date.getTime())) {
    return "";
  }
  const options: Intl.DateTimeFormatOptions = { year: "numeric", month: "long" };
  return date.toLocaleDateString(language, options);
}

export function constructInternalBlogPageUrl(locale: string, params: string) {
  return `/${locale}/blogs?${blogsInternalUrlQueryParams.QUERY_KEY}=${blogsInternalUrlQueryParams.QUERY_VALUE}&${params}`;
}

export function getLineItemAttributeLabel(value: string): string {
  const entry = Object.entries(SaleorAttributes)?.find(([, enumValue]) => enumValue === value);
  return convertToSentenceCase(entry?.[0] ?? "");
}

export const decimalSeparatedPrice = (price: number) => {
  return price?.toLocaleString("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const renderPrice = (
  lang: string,
  currencyCode?: string,
  price?: string | number,
  isNegative: boolean = false,
) => {
  if (price === undefined || price === null || price === "") return "";

  const parsedPrice = typeof price === "string" ? parseFloat(price) : price;
  const formattedPrice = parsedPrice ? decimalSeparatedPrice(Math.abs(parsedPrice)) : "";
  const sign = isNegative ? "-" : "";
  const currency = currencyCode ?? "";

  return lang === "en"
    ? `${currency} ${sign}${formattedPrice}`
    : `${formattedPrice}${sign} ${currency}`;
};

export const renderPercentage = (language: string, percent: number, isNegative?: boolean) => {
  const absPercent = Math.abs(percent)?.toFixed(2);
  const sign = isNegative ? "-" : "";
  if (language === "en") {
    return `${sign}${absPercent}%`;
  } else {
    return `${absPercent}%${sign}`;
  }
};

export const filterShippingMethods = (
  shippingMethodsFromSaleor: ShippingMethod[],
  citiesFromAzureBlob: Array<Record<string, any>>,
  areaName: string,
  cityName: string,
): ShippingMethod[] => {
  if (hasArrayElements(shippingMethodsFromSaleor) && hasArrayElements(citiesFromAzureBlob)) {
    console.log("Cities from azure blob", citiesFromAzureBlob);

    const selectedCity = citiesFromAzureBlob?.find((city) =>
      [city?.name_en, city?.name_ar].includes(cityName),
    );

    console.log("selected city from azure city blob", selectedCity);

    if (hasArrayElements(selectedCity?.areas)) {
      const selectedArea = selectedCity?.areas.find((area: Record<string, any>) =>
        [area?.name_en, area?.name_ar].includes(areaName),
      );

      if (hasArrayElements(selectedArea?.shipping_methods)) {
        const shippingMethodIds: string[] = selectedArea?.shipping_methods.map(
          (item: Record<string, any>) => item?.id,
        );

        console.log("Data after comparing saleor ids and azure blob", {
          shippingMethodIds,
          shippingMethodsFromSaleor,
          data: shippingMethodsFromSaleor.filter((each) => shippingMethodIds.includes(each?.id)),
        });

        return shippingMethodsFromSaleor.filter((each) => shippingMethodIds.includes(each?.id));
      }
    }
  }
  return [];
};

export const setCurrentPageUrlToSession = (currentPageUrl: string) => {
  const existingPageUrl = sessionStorage?.getItem(sessionStorageKeys.PREVIOUS_PAGE_URL);
  if (
    existingPageUrl !== currentPageUrl &&
    !currentPageUrl?.includes("/widget/config_iframe.html")
  ) {
    sessionStorage?.setItem(sessionStorageKeys.PREVIOUS_PAGE_URL, currentPageUrl);
  }
};

export const triggerCustomViewEvent = async (
  user: TypeSessionUser | undefined,
  locale: string,
  pageType?: string,
  pageCategory?: string,
  pageUrl?: string,
) => {
  const gaPageType = pageType
    ? analyticEventSources?.[pageType as keyof typeof analyticEventSources]
    : "";
  let pageTitle = "";
  let currentPageUrl = "";
  let previousPageUrl = "";
  if (typeof window !== "undefined") {
    pageTitle = document?.title ?? "";
    currentPageUrl = pageUrl || window?.location?.href || "";
    previousPageUrl = sessionStorage?.getItem(sessionStorageKeys.PREVIOUS_PAGE_URL) ?? "";
    setCurrentPageUrlToSession(currentPageUrl);
  }
  await handlePostAnalytics({
    eventType: analyticEventTypes.CUSTOM,
    data: {
      target: analyticEvents.CUSTOM_PAGE_VIEW,
      gaPageInfo: {
        pageType: gaPageType || pageType || "",
        pageCategory: pageCategory ?? "",
        storeId: getChannelId(locale),
        previousPageUrl: previousPageUrl ?? "",
        currentPageUrl: currentPageUrl ?? "",
        pageTitle: pageTitle ?? "",
      },
      locale,
      user,
    },
  });
};

export const createAnonymousUserObject = (
  data?: Checkout | Order | null | undefined,
  cncPersonalDetails?: PersonalDetailsInputType | null,
): TypeSessionUser => ({
  name: cncPersonalDetails
    ? `${cncPersonalDetails?.firstName ?? ""}${cncPersonalDetails?.firstName ? " " : ""}${cncPersonalDetails?.lastName ?? ""}`
    : `${data?.shippingAddress?.firstName ?? ""}${data?.shippingAddress?.firstName ? " " : ""}${data?.shippingAddress?.lastName ?? ""}`,
  anonymousUserEmail: (data && "email" in data ? data?.email : data?.userEmail) ?? "",
  given_name: cncPersonalDetails?.firstName ?? data?.shippingAddress?.firstName ?? "",
  family_name: cncPersonalDetails?.lastName ?? data?.shippingAddress?.lastName ?? "",
  phone_number: cncPersonalDetails?.phone ?? data?.shippingAddress?.phone ?? "",
});

export const triggerCartItemEvent = async (cart: CheckoutResponse | null, locale: string) => {
  await handlePostAnalytics({
    eventType: analyticEventTypes.DETAILS,
    data: {
      target: analyticEvents.CART_ITEMS,
      cart: cart?.checkout,
      currency: !hasArrayElements(cart?.checkout?.lines || []) ? getCurrencyCode(locale) : "",
      buyFlow: "",
    },
  });
};

export const isCncOrder = (data?: Order | Checkout) => {
  return data?.deliveryMethod?.__typename === shippingConstants.SHIPPING_TYPE.WARE_HOUSE;
};

export const getCookieExpiryDate = (expiry: number) => {
  const now = new Date();
  return new Date(now.getTime() + expiry * 60 * 1000);
};

export const validatePhoneNumber = (dialCode: string, value: string) => {
  if (dialCode === "+20" || dialCode === "20") {
    const egPrefixRegex = /^0?(10|11|12|15)/;
    if (egPrefixRegex.test(value)) {
      if (value.replace(/\D/g, "").length >= 10) {
        const phone = parsePhoneNumberFromString(dialCode + value);
        return phone && isValidPhoneNumber(phone.number);
      }
      return true;
    }
    return false;
  }
  const phone = parsePhoneNumberFromString(dialCode + value);
  return phone && isValidPhoneNumber(phone.number);
};

export const constructSkuCacheKey = (type: string, sku: string, locale: string): string => {
  return `${type}_${sku}_${getCountryCodeFromLocale(locale)}`;
};

export const constructSkuCacheRedisKey = (type: string, sku: string, locale: string): string => {
  return `${type}:${getCountryCodeFromLocale(locale)}:${sku}`;
};

export const isExpired = (timeStamp?: number): boolean => {
  if (!timeStamp) return true;
  return (Date.now() - timeStamp) / 1000 > revalidateTime;
};

export const getLabelTranslation = (
  language: string,
  key: string,
  translations?: Record<string, string>,
): string => {
  return translations?.[key] ?? defaultTranslations?.[key]?.[language];
};

export const translateFilterLabel = (
  language: string,
  label: string,
  facetType: string,
  translations?: Record<string, string>,
): string => {
  if (label) {
    if (facetType === facetTypeConstant.PACK_FILTER) {
      const unit = label === "1 PIECE" ? "unit_lens_per_box" : "unit_lenses_per_box";
      return label.replace("PIECE", getLabelTranslation(language, unit, translations));
    }
  }
  return label;
};

// Normalize special Alef variants to "ا"
function normalizeArabicLetter(letter: string) {
  if (!letter) return null;
  return ["أ", "إ", "آ", "ٱ"].includes(letter) ? "ا" : letter;
}

// Get 1-based index from arAlphabets
export function getArabicLetterIndex(name: string) {
  const firstLetter = normalizeArabicLetter(name?.charAt(0));

  // Handle digits (if the first letter is a number)
  if (firstLetter! >= "0" && firstLetter! <= "9") return 0;

  return arAlphabets.indexOf(firstLetter!);
}

export const PTConverter = (value: any) => {
  let protableValue = value;
  if (Array.isArray(value)) {
    protableValue = value.map((item) => {
      if (item.children && Array.isArray(item.children)) {
        const newChildren: any[] = [];
        item.children.forEach((child: any) => {
          const regex = /{{\s*CURRENCY_ICON(?:_([A-Z]{3}))?\s*}}/i;
          const globalRegex = /{{\s*CURRENCY_ICON(?:_([A-Z]{3}))?\s*}}/gi;
          if (child.text && typeof child.text === "string" && regex.test(child.text)) {
            // Split using regex with capturing group. Format: [text, currencyCode?, text, currencyCode?, text...]
            const parts = child.text.split(globalRegex);

            for (let i = 0; i < parts.length; i += 2) {
              const textPart = parts[i];
              if (textPart !== undefined && textPart !== "") {
                newChildren.push({
                  ...child,
                  _key: `${child._key || uuidv4()}-${i}-text`,
                  text: textPart.replace(/^\n/, " \n"),
                });
              }

              if (i + 1 < parts.length) {
                const currencyCode = parts[i + 1]?.toUpperCase();
                newChildren.push({
                  _type: "currencyIcon",
                  _key: `${child._key || uuidv4()}-${i}-icon`,
                  currency: currencyCode,
                });
              }
            }
          } else {
            // Preserve existing logic for newlines
            newChildren.push({ ...child, text: child.text?.replace?.(/^\n/, " \n") ?? child.text });
          }
        });
        return { ...item, children: newChildren };
      }
      return item;
    });
  }

  return protableValue;
};

/**
 * Extract color value from Algolia product attribute object
 * Handles nested structures like: { attributes: { color_family: "Black" } }
 *
 * @param attrObj - Attribute object from Algolia
 * @returns Color string or empty string
 */
export function extractColorFromAttribute(attrObj: any): string {
  if (!attrObj) return "";

  // Direct string value
  if (typeof attrObj === "string") return attrObj;

  // Check common nested structures in priority order
  // For frame colors: attributes.color_family is the primary source
  if (attrObj.attributes?.color_family) return attrObj.attributes.color_family;
  if (attrObj.color_family) return attrObj.color_family;
  if (attrObj.attributes?.label) return attrObj.attributes.label;
  if (attrObj.label) return attrObj.label;
  if (attrObj.value) return attrObj.value;

  return "";
}

/**
 * Extract product color from Algolia product data
 * Supports all product types: Sunglasses, Eyeglasses, Contact Lenses, Accessories
 * Checks multiple possible locations: selectedVariant, defaultVariant, product
 *
 * @param product - Algolia product object
 * @param selectedVariant - Currently selected variant
 * @param lang - Language code ('en' or 'ar')
 * @returns Object with color and source path
 */
export function extractProductColor(
  product: any,
  selectedVariant: any,
  lang: string,
): { color: string; source: string } {
  let productColor = "";
  let colorSource = "";

  // 1. Check selectedVariant direct properties first (for products with hexcode)
  if (!productColor && selectedVariant?.hexcode) {
    const colorAttr = selectedVariant?.attributes?.[lang] || selectedVariant?.attributes;
    if (colorAttr) {
      productColor =
        extractColorFromAttribute(colorAttr.fcCodeEnum) ||
        extractColorFromAttribute(colorAttr.frameColor) ||
        extractColorFromAttribute(colorAttr.color) ||
        extractColorFromAttribute(colorAttr.colorCode);
      if (productColor) colorSource = `selectedVariant.attributes.[color]`;
    }
  }

  // 2. Try fcCodeEnum paths for frame color (eyeglasses/sunglasses)
  // A. Check defaultVariant.attributes.fcCodeEnum (WORKS FOR SUNGLASSES)
  if (!productColor && product?.defaultVariant?.attributes?.fcCodeEnum) {
    productColor = extractColorFromAttribute(product.defaultVariant.attributes.fcCodeEnum);
    if (productColor) colorSource = "defaultVariant.attributes.fcCodeEnum";
  }
  if (!productColor && product?.defaultVariant?.attributes?.[lang]?.fcCodeEnum) {
    productColor = extractColorFromAttribute(product.defaultVariant.attributes[lang].fcCodeEnum);
    if (productColor) colorSource = `defaultVariant.attributes.${lang}.fcCodeEnum`;
  }

  // B. Check selectedVariant.attributes.fcCodeEnum (FOR EYEGLASSES)
  if (!productColor && selectedVariant?.attributes?.fcCodeEnum) {
    productColor = extractColorFromAttribute(selectedVariant.attributes.fcCodeEnum);
    if (productColor) colorSource = "selectedVariant.attributes.fcCodeEnum";
  }
  if (!productColor && selectedVariant?.attributes?.[lang]?.fcCodeEnum) {
    productColor = extractColorFromAttribute(selectedVariant.attributes[lang].fcCodeEnum);
    if (productColor) colorSource = `selectedVariant.attributes.${lang}.fcCodeEnum`;
  }

  // C. Check product.attributes.fcCodeEnum (FALLBACK)
  if (!productColor && product?.attributes?.fcCodeEnum) {
    productColor = extractColorFromAttribute(product.attributes.fcCodeEnum);
    if (productColor) colorSource = "product.attributes.fcCodeEnum";
  }
  if (!productColor && product?.attributes?.[lang]?.fcCodeEnum) {
    productColor = extractColorFromAttribute(product.attributes[lang].fcCodeEnum);
    if (productColor) colorSource = `product.attributes.${lang}.fcCodeEnum`;
  }

  // 3. Try direct frameColor/color properties
  if (!productColor && selectedVariant?.frameColor) {
    productColor = selectedVariant.frameColor;
    colorSource = "selectedVariant.frameColor";
  }
  if (!productColor && selectedVariant?.color) {
    const colorVal = selectedVariant.color;
    productColor = typeof colorVal === "string" ? colorVal : colorVal?.value;
    colorSource = "selectedVariant.color";
  }

  // 4. Try clColorEnum paths for contact lens color
  if (!productColor) {
    productColor = extractColorFromAttribute(selectedVariant?.attributes?.[lang]?.clColorEnum);
    if (productColor) colorSource = `selectedVariant.attributes.${lang}.clColorEnum`;
  }
  if (!productColor) {
    productColor = extractColorFromAttribute(selectedVariant?.attributes?.clColorEnum);
    if (productColor) colorSource = "selectedVariant.attributes.clColorEnum";
  }
  if (!productColor) {
    productColor = extractColorFromAttribute(
      product?.defaultVariant?.attributes?.[lang]?.clColorEnum,
    );
    if (productColor) colorSource = `defaultVariant.attributes.${lang}.clColorEnum`;
  }
  if (!productColor) {
    productColor = extractColorFromAttribute(product?.defaultVariant?.attributes?.clColorEnum);
    if (productColor) colorSource = "defaultVariant.attributes.clColorEnum";
  }

  // 5. Try accColorFamilyEnumCode paths for accessory color
  if (!productColor) {
    productColor = extractColorFromAttribute(
      selectedVariant?.attributes?.[lang]?.accColorFamilyEnumCode,
    );
    if (productColor) colorSource = `selectedVariant.attributes.${lang}.accColorFamilyEnumCode`;
  }
  if (!productColor) {
    productColor = extractColorFromAttribute(selectedVariant?.attributes?.accColorFamilyEnumCode);
    if (productColor) colorSource = "selectedVariant.attributes.accColorFamilyEnumCode";
  }
  if (!productColor) {
    productColor = extractColorFromAttribute(
      product?.defaultVariant?.attributes?.[lang]?.accColorFamilyEnumCode,
    );
    if (productColor) colorSource = `defaultVariant.attributes.${lang}.accColorFamilyEnumCode`;
  }
  if (!productColor) {
    productColor = extractColorFromAttribute(
      product?.defaultVariant?.attributes?.accColorFamilyEnumCode,
    );
    if (productColor) colorSource = "defaultVariant.attributes.accColorFamilyEnumCode";
  }

  // 6. Try generic color_family as last resort
  if (!productColor) {
    productColor =
      extractColorFromAttribute(selectedVariant?.attributes?.[lang]?.color_family) ||
      extractColorFromAttribute(selectedVariant?.attributes?.color_family) ||
      extractColorFromAttribute(product?.defaultVariant?.attributes?.[lang]?.color_family) ||
      extractColorFromAttribute(product?.defaultVariant?.attributes?.color_family) ||
      extractColorFromAttribute(product?.attributes?.[lang]?.color_family) ||
      extractColorFromAttribute(product?.attributes?.color_family) ||
      "";
    if (productColor) colorSource = "color_family";
  }

  return { color: productColor, source: colorSource };
}
/**
 * Get timezone string from locale
 * @param locale - Locale string (e.g., "ae-en", "sa-ar")
 * @returns Timezone string (e.g., "Asia/Dubai", "Asia/Riyadh")
 */
export const getTimezoneFromLocale = (locale: string): string => {
  const countryCode = getCountryCodeFromLocale(locale)?.toUpperCase();

  // Map country codes to timezones
  const timezoneMap: Record<string, string> = {
    // Middle East & Gulf
    AE: "Asia/Dubai",
    SA: "Asia/Riyadh",
    EG: "Africa/Cairo",
    QA: "Asia/Qatar",
    KW: "Asia/Kuwait",
    OM: "Asia/Muscat",
    BH: "Asia/Bahrain",
    JO: "Asia/Amman",
    LB: "Asia/Beirut",
    IQ: "Asia/Baghdad",
    YE: "Asia/Aden",
    IL: "Asia/Jerusalem",
    PS: "Asia/Gaza",
    IR: "Asia/Tehran",
    TR: "Europe/Istanbul",

    // North Africa
    MA: "Africa/Casablanca",
    TN: "Africa/Tunis",
    DZ: "Africa/Algiers",
    LY: "Africa/Tripoli",
    SD: "Africa/Khartoum",
    ET: "Africa/Addis_Ababa",
    SO: "Africa/Mogadishu",
    DJ: "Africa/Djibouti",
    ER: "Africa/Asmara",

    // South Asia
    PK: "Asia/Karachi",
    IN: "Asia/Kolkata",
    BD: "Asia/Dhaka",
    LK: "Asia/Colombo",
    NP: "Asia/Kathmandu",
    AF: "Asia/Kabul",

    // Southeast Asia
    SG: "Asia/Singapore",
    MY: "Asia/Kuala_Lumpur",
    TH: "Asia/Bangkok",
    ID: "Asia/Jakarta",
    PH: "Asia/Manila",
    VN: "Asia/Ho_Chi_Minh",
    MM: "Asia/Yangon",
    KH: "Asia/Phnom_Penh",
    LA: "Asia/Vientiane",

    // East Asia
    CN: "Asia/Shanghai",
    JP: "Asia/Tokyo",
    KR: "Asia/Seoul",
    HK: "Asia/Hong_Kong",
    TW: "Asia/Taipei",
    MO: "Asia/Macau",
    MN: "Asia/Ulaanbaatar",

    // Europe
    GB: "Europe/London",
    FR: "Europe/Paris",
    DE: "Europe/Berlin",
    IT: "Europe/Rome",
    ES: "Europe/Madrid",
    NL: "Europe/Amsterdam",
    BE: "Europe/Brussels",
    CH: "Europe/Zurich",
    AT: "Europe/Vienna",
    SE: "Europe/Stockholm",
    NO: "Europe/Oslo",
    DK: "Europe/Copenhagen",
    FI: "Europe/Helsinki",
    PL: "Europe/Warsaw",
    CZ: "Europe/Prague",
    GR: "Europe/Athens",
    PT: "Europe/Lisbon",
    IE: "Europe/Dublin",
    RU: "Europe/Moscow",
    UA: "Europe/Kyiv",

    // Americas
    US: "America/New_York", // Eastern Time (default, can be overridden)
    CA: "America/Toronto",
    MX: "America/Mexico_City",
    BR: "America/Sao_Paulo",
    AR: "America/Buenos_Aires",
    CL: "America/Santiago",
    CO: "America/Bogota",
    PE: "America/Lima",
    VE: "America/Caracas",

    // Oceania
    AU: "Australia/Sydney",
    NZ: "Pacific/Auckland",

    // Africa (additional)
    ZA: "Africa/Johannesburg",
    KE: "Africa/Nairobi",
    NG: "Africa/Lagos",
    GH: "Africa/Accra",
    TZ: "Africa/Dar_es_Salaam",
    UG: "Africa/Kampala",
  };

  return timezoneMap[countryCode || ""] || "Asia/Dubai"; // Default to Dubai
};

/**
 * Check if current environment is production.
 */
export const isProductionEnvironment = (): boolean => {
  const env = (process.env.ENVIRONMENT || "").toLowerCase();
  return env === "prod";
};


/**
 * Extracts and translates the promotion badge name safely.
 * Respects the showBadgeInStorefront toggle.
 * 
 * @param promotion The promotion object
 * @param language The current language code (e.g., 'en', 'ar')
 * @returns The localized promotion name, or an empty string if it shouldn't be shown
 */
export const getPromotionBadgeName = (promotion: any, language: string): string => {
  if (!promotion) return "";
  
  if (promotion.showBadgeInStorefront === false) {
    return "";
  }
  
  return (language === "ar" 
    ? promotion?.translation?.name ?? promotion?.name 
    : promotion?.name) ?? "";
};
